import { Color, GlobalColor, GradientColorV } from "@/types/colors";
import slugify from "slugify";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { v4 as uuidV4 } from "uuid";
import lodash from "lodash";
import { globalStore } from "@/store/store-accessor";

// https://stackoverflow.com/a/51567564
export function isLight(color: string) {
  if (!isValidHex(color)) return true;

  const hex = expandHexColor(color);

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const alpha = parseInt(hex.substr(6, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 230 || alpha < 100;
}

export function stripColorSign(color: string) {
  return color.replace("#", "");
}

/*
 Expand 3-digit hex code to 6-digit hex codes
 #f0f -> #ff00ff
 #fff -> #ffffff
*/
export function expandHexColor(color: string) {
  const hex = stripColorSign(color);
  if (hex.length !== 3) return hex;

  return hex
    .split("")
    .map(hex => hex + hex)
    .join("");
}

export function isValidHex(color: string) {
  const hex = expandHexColor(color);
  return (hex.length === 6 || hex.length === 8) && !isNaN(Number("0x" + hex));
}

export function isCSSVariable(color: Color) {
  return color.value.startsWith("var(--");
}

export function findColorByVariableName(color: Color, colors: GlobalColor[]) {
  if (!isCSSVariable(color)) return undefined;
  const name = extractVariableName(color.value);
  return colors.find(c => c.cssVariableName === name);
}

export function extractVariableName(color: string) {
  const regexp = /var\(--([a-zA-Z0-9-_]+)\s*(?:,.*)?\)/;
  const match = regexp.exec(color);

  if (match !== null) {
    return match[1].trim();
  }

  return null;
}

export function createVariableName(color: string, suffix?: string | number) {
  const prefix = "bde-palette-";
  const append = suffix ? `-${suffix}` : "";
  const slug = slugify(color, { lower: true });
  const uuid = uuidV4();

  return `${prefix}${slug}-${uuid}${append}`;
}

export function getGlobalColorCode(color: GlobalColor): string {
  return pipe(
    color.value,
    GradientColorV.decode,
    fold(
      () => color.value as string,
      gradient => gradient.value
    )
  );
}

export function isValueAGradient(color: unknown) {
  return pipe(
    color,
    GradientColorV.decode,
    fold(
      () => false,
      () => true
    )
  );
}

export function isGlobalColorAGradient(color: GlobalColor) {
  return isValueAGradient(color.value);
}

export function getDefaultColors() {
  const defaultColors = [
    {
      label: "Brand",
      path: "settings.colors.brand",
      cssVariableName: "bde-brand-primary-color"
    },
    {
      label: "Text",
      path: "settings.colors.text",
      cssVariableName: "bde-body-text-color"
    },
    {
      label: "Headings",
      path: "settings.colors.headings",
      cssVariableName: "bde-headings-color"
    },
    {
      label: "Links",
      path: "settings.colors.links",
      cssVariableName: "bde-links-color"
    },
    {
      label: "Background",
      path: "settings.colors.background",
      cssVariableName: "bde-background-color"
    }
  ];

  return defaultColors
    .map(color => {
      return {
        label: color.label,
        cssVariableName: color.cssVariableName,
        value: lodash.get(globalStore.globalSettings, color.path)
      };
    })
    .filter(c => c.value);
}

export function getGlobalAndDefaultColors(): GlobalColor[] {
  return [
    ...getDefaultColors(),
    ...globalStore.globalColors,
    ...globalStore.globalGradients
  ];
}
